import { GOOGLE_EMBED_API_KEY } from "appConfiguration";
import { EMPTY_VALUE } from "staticConfiguration";
import CalendarMonthIcon from "@mui/icons-material/CalendarMonth";
import DownloadIcon from "@mui/icons-material/Download";
import LocationOn from "@mui/icons-material/LocationOn";
import OpenInNew from "@mui/icons-material/OpenInNew";
import PriorityHighRoundedIcon from "@mui/icons-material/PriorityHighRounded";
import PublishIcon from "@mui/icons-material/Publish";
import Box from "@mui/material/Box";
import Breadcrumbs from "@mui/material/Breadcrumbs";
import Button from "@mui/material/Button";
import Chip from "@mui/material/Chip";
import Divider from "@mui/material/Divider";
import FormControlLabel from "@mui/material/FormControlLabel";
import Stack from "@mui/material/Stack";
import Switch from "@mui/material/Switch";
import Typography from "@mui/material/Typography";
import { CustomLink } from "core/components/CustomLink";
import { ErrorComponent } from "core/components/ErrorComponent";
import { PageContainer } from "core/components/PageContainer";
import { PageHeader } from "core/components/PageHeader";
import { useCentrixApi } from "core/hooks/useCentrixApi";
import { useFormatDate } from "core/hooks/useFormatDate";
import { usePageLogging } from "core/hooks/usePageLogging";
import { buildQueryString } from "core/utils/buildQueryString";
import { FavoriteButton } from "features/MyLinks/components";
import { CTAButton } from "features/NewsEvents/components/CTAButton";
import { getImageSRC } from "features/NewsEvents/utils";
import { useMarkNotificationReadByItemIdOnMount } from "features/Notifications/useMarkNotificationReadByItemIdOnMount";
import Image from "next/legacy/image";
import { useRouter } from "next/router";
import { parseAsBoolean, useQueryState } from "nuqs";
import { useTranslation } from "react-i18next";
import { ArticlePageLoadingSkeleton } from "../ArticlePageLoadingSkeleton";

export function SingleEventPageView({ canEdit }: { canEdit: boolean }) {
  const {
    locale,
    query: { id },
  } = useRouter();
  const [editMode, setEditMode] = useQueryState(
    "edit",
    parseAsBoolean.withDefault(false)
  );
  const { t } = useTranslation("NewsEvents", {
    keyPrefix: "Next:NewsEvents:SingleEventPage",
  });
  useMarkNotificationReadByItemIdOnMount(id);
  usePageLogging({
    itemType: "Event",
    itemId: String(id),
    componentName: "SingleEventPage",
  });
  const formatDateWithTimeZone = useFormatDate({
    timeZoneName: "short",
    hour: "2-digit",
    minute: "2-digit",
    month: "long",
    day: "numeric",
    year: "numeric",
  });
  const formatDate = useFormatDate({
    dateStyle: "long",
    timeStyle: "short",
  });

  const { data, isError, isLoading } = useCentrixApi({
    path: "/api/news-events/articles-v2/Events/public/{id}",
    parameters: {
      path: { id: id as string },
      query: { Locale: locale! },
    },
  });
  const formattedPublish = t("publishedTime", {
    date: data?.publishedTime
      ? formatDateWithTimeZone(data.publishedTime)
      : EMPTY_VALUE,
  });

  if (isError) {
    return (
      <PageContainer>
        <ErrorComponent apiError={isError} />
      </PageContainer>
    );
  }
  if (isLoading || !data) {
    return <ArticlePageLoadingSkeleton />;
  }

  const {
    startDate,
    endDate,
    banner,
    articleCategory,
    articleType,
    content,
    title,
    address,
    isImportant,
    locationDescription,
    tags = [],
    buttonText,
    buttonUrl,
    url,
    blurb,
  } = data;

  const blurDataUrl = banner?.blurDataUrl ?? "";
  const altText = banner?.altText ?? "";
  const blobName = banner?.blobName ?? "";
  const description = banner?.description ?? "";
  const height = banner?.height ?? 0;
  const width = banner?.width ?? 0;

  const imageSRC = blobName ? getImageSRC(blobName) : "";
  const formattedStartDate = startDate ? formatDate(startDate) : EMPTY_VALUE;

  const formattedDate = endDate
    ? t("dateRange", {
        startDate: formattedStartDate,
        endDate: formatDateWithTimeZone(endDate),
      })
    : formattedStartDate;

  return (
    <PageContainer maxWidth="md">
      <PageHeader
        TopSlot={
          <Breadcrumbs separator=">">
            <CustomLink href="/events">{t("eventsLink")}</CustomLink>
            {articleCategory ? (
              <CustomLink href={`/events?categories=${articleCategory.id}`}>
                {articleCategory.title}
              </CustomLink>
            ) : (
              <CustomLink href="/events?categories=uncategorized">
                {t("uncategorized")}
              </CustomLink>
            )}
            <span />
          </Breadcrumbs>
        }
        TitleSlot={
          <FavoriteButton
            itemType="Event"
            itemId={String(id)}
            title={title ?? ""}
          />
        }
        RightSlot={
          canEdit && (
            <Stack alignItems="end">
              <FormControlLabel
                control={<Switch />}
                checked={editMode}
                onChange={(_, checked) => setEditMode(checked)}
                labelPlacement="start"
                label={t("editToggleTooltipTitle", "Quick Edit")}
              />
            </Stack>
          )
        }
        title={title ?? ""}
        titleStackProps={{ mb: 1 }}
        subTitle={
          <Box
            mt={1}
            ml={1}
            display="grid"
            gridTemplateColumns="auto 1fr"
            columnGap={1}
            rowGap={0.5}
            alignItems="center"
            color="text.secondary"
          >
            <PublishIcon fontSize="small" color="inherit" />
            <Typography variant="body2">{formattedPublish}</Typography>
            <CalendarMonthIcon fontSize="small" />
            <Typography variant="body2">{formattedDate}</Typography>
            {address && (
              <CustomLink
                display="contents"
                target="_blank"
                href={`https://www.google.com/maps/search/${encodeURIComponent(
                  address
                )}`}
              >
                <LocationOn fontSize="small" />
                <Typography gutterBottom variant="body2" margin={0}>
                  {locationDescription
                    ? `${address} - ${locationDescription}`
                    : address}
                </Typography>
              </CustomLink>
            )}
            {url && (
              <CustomLink target="_blank" href={url} display="contents">
                <OpenInNew fontSize="small" />
                <Typography gutterBottom variant="body2" margin={0}>
                  {url}
                </Typography>
              </CustomLink>
            )}
          </Box>
        }
      />
      <Box position="relative">
        <Image
          src={imageSRC}
          alt={altText ?? ""}
          blurDataURL={blurDataUrl ?? ""}
          placeholder="blur"
          layout="responsive"
          height={height}
          width={width}
          objectFit="contain"
        />
        {isImportant && (
          <PriorityHighRoundedIcon
            sx={{
              zIndex: 1,
              position: "absolute",
              top: ({ spacing }) => spacing(1),
              left: ({ spacing }) => spacing(1),
              color: "red",
            }}
          />
        )}
        {articleType && (
          <CustomLink href={`/events?types=${articleType.id}`}>
            <Chip
              clickable
              color="primary"
              label={articleType.title}
              sx={{
                zIndex: 1,
                position: "absolute",
                top: ({ spacing }) => spacing(2),
                right: ({ spacing }) => spacing(2),
              }}
            />
          </CustomLink>
        )}
      </Box>
      <Typography
        sx={{ mt: 1, ml: 1 }}
        variant="caption"
        color="text.secondary"
      >
        {description}
      </Typography>
      <Divider sx={{ my: 4 }} />
      {/** biome-ignore lint/security/noDangerouslySetInnerHtml: content is generated by a trusted source */}
      <Box dangerouslySetInnerHTML={{ __html: content ?? "" }} />
      <Stack direction="row" mt={2} gap={4} justifyContent="center">
        <Button
          variant="outlined"
          sx={{ borderRadius: "16px" }}
          color="primary"
          href={`/api/ics/${title}?${buildQueryString({
            startDate,
            endDate,
            description: blurb,
            location: address ?? url,
          })}`}
          target="_blank"
          startIcon={<DownloadIcon />}
        >
          {t("downloadICS")}
        </Button>
        {buttonText && buttonUrl && (
          <CTAButton href={buttonUrl} text={buttonText} />
        )}
      </Stack>
      {address && (
        <>
          <Divider sx={{ my: 4 }} />
          <Box position="relative" width="100%" sx={{ aspectRatio: "16 / 9" }}>
            {GOOGLE_EMBED_API_KEY ? (
              <iframe
                title="event-address"
                width="500px"
                height="500px"
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
                style={{
                  border: "none",
                  top: 0,
                  left: 0,
                  bottom: 0,
                  right: 0,
                  width: "100%",
                  height: "100%",
                }}
                src={`https://www.google.com/maps/embed/v1/place?key=${GOOGLE_EMBED_API_KEY}&q=${encodeURIComponent(
                  address
                )}`}
              />
            ) : (
              <Typography color="error.main">
                Google Maps Embed API Key not configured
              </Typography>
            )}
          </Box>
        </>
      )}
      <Divider sx={{ my: 4 }} />

      <Stack
        direction="row"
        spacing={2}
        justifyContent="space-evenly"
        alignItems="flex-start"
      >
        {Array.isArray(tags) && tags.length > 0 && (
          <Stack spacing={1} justifyContent="center">
            <Typography variant="body1" textAlign="center">
              {t("tags")}
            </Typography>
            <Stack spacing={1} justifyContent="center" direction="row">
              {tags.map((tag) => (
                <CustomLink key={tag} href={`/events?tags=${tag}`}>
                  #{tag}
                </CustomLink>
              ))}
            </Stack>
          </Stack>
        )}
      </Stack>
    </PageContainer>
  );
}
